import { useCallback, useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line no-restricted-imports
import { Checkbox, Divider, Form } from 'semantic-ui-react';
import { FormComponentProps, GenderEnum } from '../../../common';
import { canSendInvitation, useContacts } from '../../../contacts';
import { dateFormatter, formatISOStringAsUtcDateOrNull } from '../../../helpers/dates';
import { InterestedFamilyType, useInterestedFamilies } from '../../../integrations';
import InterestedFamiliesForm from '../../../students/components/InterestedFamiliesForm';
import { ParentType, StudentAndParentsType, StudentType } from '../../../students/types';
import EnrollmentParentsForm from './EnrollmentParentsForm';
import EnrollmentStudentEditForm from './EnrollmentStudentEditForm';

type EnrollmentStudentAndParentsFormProps = FormComponentProps<StudentAndParentsType> & {
  parents: ParentType[];
  studentAndParents?: StudentAndParentsType;
  marketplaceChildId?: number;
  onInterestedFamilySelected?: (interestedFamily: InterestedFamilyType | undefined) => void;
  form: UseFormReturn<StudentAndParentsType>;
};

function EnrollmentStudentAndParentsForm({
  title,
  hasTitle = true,
  onInterestedFamilySelected,
  marketplaceChildId,
  form,
  parents,
}: EnrollmentStudentAndParentsFormProps) {
  const { t } = useTranslation();
  const { familyContacts } = useContacts(); // get all existing org/contacts
  const interestedFamilies = useInterestedFamilies();

  const [newOrInterested, setNewOrInterested] = useState<string>('new');
  const [wasInterestedFamilySelected, setWasInterestedFamilySelected] = useState<boolean>(false);

  const viewTitle = hasTitle ? t(title || 'students.studentInformationFormTitle') : '';

  const onSelectInterestedFamily = useCallback(
    (interestedFamily: InterestedFamilyType) => {
      const studentAndParents = createStudentAndParentsFromInterestedFamily(interestedFamily, familyContacts);
      form.reset(studentAndParents);
      setWasInterestedFamilySelected(true);
      onInterestedFamilySelected?.(interestedFamily);
    },
    [onInterestedFamilySelected, familyContacts, form]
  );

  useEffect(() => {
    if (marketplaceChildId && interestedFamilies) {
      const interestedFamily = interestedFamilies.find((family) => family.student?.id === marketplaceChildId);
      if (interestedFamily) onSelectInterestedFamily(interestedFamily);
    }
  }, [marketplaceChildId, interestedFamilies, onSelectInterestedFamily]);

  const onSelectNewOrInterested = useCallback(
    (e: any, { value }: any) => {
      setNewOrInterested(value);
      if (value === 'new') {
        const defaultValues = {
          student: {} as StudentType,
          parents: [{} as ParentType],
        };
        form.reset(defaultValues);
        setWasInterestedFamilySelected(false);
        onInterestedFamilySelected?.(undefined);
      }
    },
    [onInterestedFamilySelected, form]
  );

  const shouldShowInterestedFamiliesForm = useMemo(() => {
    return newOrInterested === 'interested' && !wasInterestedFamilySelected;
  }, [newOrInterested, wasInterestedFamilySelected]);

  return (
    <Form className="student-and-parents-form" data-testid="student-and-parents-form">
      {!!viewTitle && (
        <h2 data-testid="student-and-parents-form-title" className="mb-4 text-lg font-semibold">
          {viewTitle}
        </h2>
      )}
      <Form.Group>
        <Form.Field>
          <Checkbox
            radio
            label={t('students.newStudentLabel')}
            name="newOrInterested"
            value="new"
            checked={newOrInterested === 'new'}
            onChange={onSelectNewOrInterested}
            data-testid="onSelectNew"
          />
        </Form.Field>
        <Form.Field>
          <Checkbox
            radio
            label={t('students.interestedFamiliesLabel')}
            name="newOrInterested"
            value="interested"
            checked={newOrInterested === 'interested'}
            onChange={onSelectNewOrInterested}
            data-testid="onSelectInterested"
          />
          {newOrInterested === 'interested' && wasInterestedFamilySelected && (
            <>
              <span> (</span>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a href="#" onClick={() => setWasInterestedFamilySelected(false)} data-testid="selectedinterestedfamily">
                {t('students.interestedFamiliesSelectDifferentLabel')}
              </a>
              <span>) </span>
            </>
          )}
        </Form.Field>
      </Form.Group>
      {!shouldShowInterestedFamiliesForm && (
        <div key="newStudent">
          <EnrollmentStudentEditForm hasTitle={false} form={form} />
          <Divider />
          <EnrollmentParentsForm form={form} parents={parents} />
        </div>
      )}
      {shouldShowInterestedFamiliesForm && (
        <InterestedFamiliesForm key="newStudent" hasTitle={false} onSelect={onSelectInterestedFamily} />
      )}
    </Form>
  );
}

function createStudentAndParentsFromInterestedFamily(
  interestedFamily: InterestedFamilyType,
  familyContacts: any[]
): StudentAndParentsType {
  const { student, parents, family_id } = interestedFamily;
  const newParents = parents?.map((p) => {
    const existingContact = familyContacts?.find((c) => c.email === p.email);
    return {
      contact: existingContact,
      firstName: p.firstName ?? '',
      lastName: p.lastName ?? '',
      email: p.email,
      phone: p.phone ?? '',
      isInterestedFamily: true,
      shouldSendInvitation: canSendInvitation(existingContact),
      marketplaceFamilyId: family_id ?? undefined,
      // uid is required for UPE to show documents and invoices
      // this relies on the Marketplace Leads API using the deprecated
      // families entities to source the parents for a lead. the parents
      // are actually records in public.users which means that they definitely
      // have an account that they have logged in to before
      uid: p.identityId,
      // this is required so that a new contact's status is set to verified
      // when they have an identityId
      hasAuthUser: !!p.identityId,
    };
  });

  // Map gender string to enum
  const mapGender = (gender?: string) => {
    if (!gender) return undefined;
    switch (gender) {
      case 'boy':
        return GenderEnum.MALE;
      case 'girl':
        return GenderEnum.FEMALE;
      case 'other':
        return GenderEnum.OTHER;
      default:
        return undefined;
    }
  };

  return {
    student: {
      firstName: student?.firstName ?? '',
      lastName: student?.lastName ?? '',
      gender: mapGender(student?.gender),
      birthday: student?.dob ? dateFormatter(formatISOStringAsUtcDateOrNull(student.dob), 'YYYY-MM-DD') : null,
    },
    parents: newParents ?? [],
    interestedFamily,
  };
}

export default EnrollmentStudentAndParentsForm;
